import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../utils/supabase';
import AuthAside from './AuthAside';
import './AuthPages.css';

/** Permet de remplacer le mot de passe après validation de la session par Supabase. */
export default function ResetPasswordForm() {
  const [ready, setReady] = useState(false);
  const [checking, setChecking] = useState(true);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  /** Attend le traitement du lien par le SDK, vérifie la session et retire l'écouteur au départ de la page. */
  useEffect(() => {
    let active = true;
    const hasLinkError = new URLSearchParams(window.location.hash.slice(1)).has('error')
      || new URLSearchParams(window.location.search).has('error');

    /** Met à jour l'accès uniquement lorsque la page est encore affichée et le lien sans erreur. */
    function updateAccess(session) {
      if (!active) return;
      setReady(Boolean(session) && !hasLinkError);
      setChecking(false);
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Ces événements couvrent le retour par email, le chargement initial et l'expiration de session.
      if (['PASSWORD_RECOVERY', 'INITIAL_SESSION', 'SIGNED_OUT'].includes(event)) updateAccess(session);
    });

    /** Récupère la session même si l'événement de récupération a précédé le montage du composant. */
    async function checkSession() {
      try {
        const { data, error: sessionError } = await supabase.auth.getSession();
        updateAccess(sessionError ? null : data?.session);
      } catch {
        updateAccess(null);
      }
    }
    checkSession();

    /** Évite toute mise à jour après démontage et libère l'abonnement Supabase. */
    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  /** Vérifie la confirmation puis demande à Supabase de modifier le mot de passe de l'utilisateur authentifié. */
  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    if (!ready || loading || done) return;
    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }
    if (password !== confirmation) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      setDone(true);
      setPassword('');
      setConfirmation('');
    } catch (updateError) {
      setError(updateError?.code === 'same_password'
        ? 'Choisissez un mot de passe différent du précédent.'
        : 'Impossible de modifier le mot de passe. Vérifiez les exigences de votre compte ou demandez un nouveau lien.');
    } finally {
      setLoading(false);
    }
  }

  /** Actualise le nouveau mot de passe et efface une éventuelle erreur de saisie. */
  function handlePasswordChange(event) { setPassword(event.target.value); setError(''); }

  /** Actualise la confirmation sans empêcher le collage ou les gestionnaires de mots de passe. */
  function handleConfirmationChange(event) { setConfirmation(event.target.value); setError(''); }

  return (
    <div className="auth-page">
      <div className="auth-shell">
        <AuthAside titre="Protégez l’accès à votre" motCle="compte" points={['Choisissez un mot de passe unique', 'Conservez-le dans votre gestionnaire de mots de passe']} />
        <section className="auth-card">
          <h2>Nouveau mot de passe</h2>
          <p className="auth-sub">Choisissez puis confirmez votre nouveau mot de passe.</p>
          {checking ? <p role="status">Vérification du lien…</p> : done ? (
            <>
              <div className="auth-alert auth-alert-success" role="status">Votre mot de passe a été modifié.</div>
              <p className="auth-switch"><Link to="/app/dashboard">Accéder à mon espace</Link></p>
            </>
          ) : !ready ? (
            <div className="auth-alert auth-alert-error" role="alert">Ce lien est invalide ou a expiré. <Link to="/forgot-password">Demander un nouveau lien</Link></div>
          ) : (
            <form className="auth-form" onSubmit={handleSubmit} aria-busy={loading}>
              {error && <div className="auth-alert auth-alert-error" role="alert">{error}</div>}
              <label className="auth-label" htmlFor="new-password">Nouveau mot de passe (6 caractères minimum)</label>
              <div className="auth-field">
                <LockClosedIcon className="auth-field-icon" aria-hidden="true" />
                <input id="new-password" type="password" autoComplete="new-password" minLength={6} required value={password} onChange={handlePasswordChange} disabled={loading} />
              </div>
              <label className="auth-label" htmlFor="confirm-password">Confirmer le mot de passe</label>
              <div className="auth-field">
                <LockClosedIcon className="auth-field-icon" aria-hidden="true" />
                <input id="confirm-password" type="password" autoComplete="new-password" minLength={6} required value={confirmation} onChange={handleConfirmationChange} disabled={loading} />
              </div>
              <button className="auth-btn auth-btn-accent" type="submit" disabled={loading}>{loading ? 'Modification en cours…' : 'Enregistrer le mot de passe'}</button>
            </form>
          )}
          <p className="auth-switch"><Link to="/login">Retour à la connexion</Link></p>
        </section>
      </div>
    </div>
  );
}
